import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayCircleIcon, SunIcon, WifiIcon, ShieldIcon, ChevronDownIcon } from 'lucide-react';
const systems = [{
  id: 'entertainment',
  title: 'Entertainment',
  tagline: 'Every room becomes a venue.',
  icon: PlayCircleIcon,
  color: '#6366F1',
  bgClass: 'bg-[#6366F1]',
  textClass: 'text-[#6366F1]',
  borderClass: 'border-[#6366F1]',
  content: [{
    name: 'Audio',
    desc: 'Reference-grade invisible speakers, multi-room distribution, Dolby Atmos'
  }, {
    name: 'Theatre',
    desc: 'Private cinema with 4K/8K projection, acoustic treatment, motorized seating'
  }, {
    name: 'Streaming',
    desc: 'Lossless audio streaming, 4K video distribution, content everywhere'
  }]
}, {
  id: 'ambience',
  title: 'Ambience',
  tagline: 'Light and shade that breathe with you.',
  icon: SunIcon,
  color: '#F59E0B',
  bgClass: 'bg-[#F59E0B]',
  textClass: 'text-[#F59E0B]',
  borderClass: 'border-[#F59E0B]',
  content: [{
    name: 'Lighting',
    desc: 'Circadian rhythm, scene-based, architectural accent, tunable white'
  }, {
    name: 'Shading',
    desc: 'Motorized blinds, blackout for cinema, daylight harvesting'
  }]
}, {
  id: 'infrastructure',
  title: 'Infrastructure',
  tagline: 'The invisible backbone of everything.',
  icon: WifiIcon,
  color: '#3B82F6',
  bgClass: 'bg-[#3B82F6]',
  textClass: 'text-[#3B82F6]',
  borderClass: 'border-[#3B82F6]',
  content: [{
    name: 'Network',
    desc: 'Enterprise WiFi mesh, dedicated AV VLAN, zero-latency backbone'
  }, {
    name: 'Control',
    desc: 'Centralized processor, one app, voice control, remote access'
  }]
}, {
  id: 'protection',
  title: 'Protection',
  tagline: 'Peace of mind that never sleeps.',
  icon: ShieldIcon,
  color: '#64748B',
  bgClass: 'bg-[#64748B]',
  textClass: 'text-[#64748B]',
  borderClass: 'border-[#64748B]',
  content: [{
    name: 'CCTV',
    desc: 'AI-powered cameras, facial recognition, cloud recording'
  }, {
    name: 'Access',
    desc: 'Biometric entry, smart locks, visitor management'
  }, {
    name: 'Alerts',
    desc: 'Instant notifications, intrusion detection, emergency protocols'
  }]
}];
export function SystemsShowcase() {
  const [expandedId, setExpandedId] = useState<string | null>('entertainment');
  return <section className="py-32 bg-obsidian relative border-t border-graphite" data-id="element-501">
      <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-502">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-503">
          <h2 className="text-headline font-serif text-platinum mb-4" data-id="element-504">
            What Powers Your Home
          </h2>
          <p className="text-silver text-lg italic" data-id="element-505">
            Not systems. Superpowers.
          </p>
        </motion.div>

        <div className="space-y-4" data-id="element-506">
          {systems.map((system, idx) => {
          const isExpanded = expandedId === system.id;
          const Icon = system.icon;
          return <motion.div key={system.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className={`bg-carbon rounded-2xl overflow-hidden transition-all duration-500 border ${isExpanded ? `${system.borderClass} shadow-[0_0_30px_rgba(0,0,0,0.5)]` : 'border-graphite hover:border-silver/30'}`} style={isExpanded ? {
            boxShadow: `0 0 30px ${system.color}20`
          } : {}} data-id="element-507">
                <button onClick={() => setExpandedId(isExpanded ? null : system.id)} className="w-full px-8 py-6 flex items-center justify-between text-left focus:outline-none" data-id="element-508">
                  <div className="flex items-center space-x-6" data-id="element-509">
                    <div className={`p-4 rounded-full ${isExpanded ? `${system.bgClass} bg-opacity-20` : 'bg-void border border-graphite'} transition-colors`} data-id="element-510">
                      <Icon className={`w-6 h-6 ${isExpanded ? system.textClass : 'text-silver'}`} data-id="element-511" />
                    </div>
                    <div data-id="element-512">
                      <h3 className={`font-serif text-2xl mb-1 ${isExpanded ? 'text-platinum' : 'text-silver'}`} data-id="element-513">
                        {system.title}
                      </h3>
                      <p className={`text-sm ${isExpanded ? system.textClass : 'text-ash'}`} data-id="element-514">
                        {system.tagline}
                      </p>
                    </div>
                  </div>
                  <motion.div animate={{
                rotate: isExpanded ? 180 : 0
              }} transition={{
                duration: 0.3
              }} data-id="element-515">
                    <ChevronDownIcon className={`w-6 h-6 ${isExpanded ? system.textClass : 'text-silver'}`} data-id="element-516" />
                  </motion.div>
                </button>

                <AnimatePresence data-id="element-517">
                  {isExpanded && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.4,
                ease: 'easeInOut'
              }} data-id="element-518">
                      <div className="px-8 pb-8 pt-2" data-id="element-519">
                        <div className="pl-20 space-y-6" data-id="element-520">
                          {system.content.map((item, i) => <div key={i} className="relative" data-id="element-521">
                              {/* Decorative line connecting items */}
                              {i !== system.content.length - 1 && <div className="absolute left-[5px] top-6 bottom-[-24px] w-px bg-graphite" data-id="element-522" />}
                              <div className="flex items-start" data-id="element-523">
                                <div className={`w-3 h-3 rounded-full ${system.bgClass} mt-1.5 mr-4 shrink-0 shadow-[0_0_10px_currentColor]`} style={{
                          color: system.color
                        }} data-id="element-524" />
                                <div data-id="element-525">
                                  <h4 className="text-platinum font-medium mb-1" data-id="element-526">
                                    {item.name}
                                  </h4>
                                  <p className="text-silver text-sm leading-relaxed" data-id="element-527">
                                    {item.desc}
                                  </p>
                                </div>
                              </div>
                            </div>)}
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}